import Dagre from '@dagrejs/dagre';
import type { Edge, XYPosition } from '@xyflow/react';
import {
	ENTITY_NODE_PADDING,
	ENTITY_NODE_WIDTH,
} from '../components/EntityNode/EntityNode.constants';
import { ENTITY_CHILDREN_ROW_HEIGHT } from '../components/EntityNodeChildren/EntityNodeChildren.constants';
import { ENTITY_NODE_HEADER_HEIGHT } from '../components/EntityNodeHeader/EntityNodeHeader.constants';
import {
	DAGRE_EDGE_SEP,
	DAGRE_NODE_SEP,
	DAGRE_RANK_SEP,
} from '../components/ERDFlow/ERDFlow.constants';
import type { ERDFlowNode } from '../components/TableNode/TableNode.types';

export const isNodeBelow = ({
	positionA,
	positionB,
}: {
	positionA: XYPosition;
	positionB: XYPosition;
}): boolean => {
	// Check if A is below B vertically
	const isBelow = positionA.y > positionB.y;

	if (!isBelow) {
		return false;
	}

	// Check if there's horizontal overlap between the nodes
	const aLeftEdge = positionA.x;
	const aRightEdge = positionA.x + ENTITY_NODE_WIDTH;
	const bLeftEdge = positionB.x;
	const bRightEdge = positionB.x + ENTITY_NODE_WIDTH;

	// Horizontal overlap occurs when A's left edge is less than B's right edge
	// AND A's right edge is greater than B's left edge
	const hasHorizontalOverlap = aLeftEdge < bRightEdge && aRightEdge > bLeftEdge;

	return isBelow && hasHorizontalOverlap;
};

export const calculateTableNodeHeight = (node: ERDFlowNode) => {
	const height =
		ENTITY_NODE_HEADER_HEIGHT +
		2 * ENTITY_NODE_PADDING +
		node.data.columns_with_relationships.length * ENTITY_CHILDREN_ROW_HEIGHT +
		node.data.columns_without_relationships.length * ENTITY_CHILDREN_ROW_HEIGHT;

	return height;
};
export const calculateTableNodeColumnsHeight = ({
	count,
}: {
	count: number;
}): number => count * ENTITY_CHILDREN_ROW_HEIGHT;

export const calculateLayout = ({
	nodes,
	edges,
}: {
	nodes: ERDFlowNode[];
	edges: Edge[];
}): {
	updatedGraph: Dagre.graphlib.Graph;
	updatedNodes: ERDFlowNode[];
} => {
	const updatedGraph = new Dagre.graphlib.Graph()
		.setDefaultEdgeLabel(() => ({}))
		.setDefaultNodeLabel(() => ({}));

	updatedGraph.setGraph({
		rankdir: 'LR',
		nodesep: DAGRE_NODE_SEP,
		edgesep: DAGRE_EDGE_SEP,
		ranksep: DAGRE_RANK_SEP,
	});

	const nodeIdSet = new Set<string>();

	nodes.forEach((node) => {
		updatedGraph.setNode(node.id, {
			width: node.measured?.width ?? ENTITY_NODE_WIDTH,
			height: calculateTableNodeHeight(node),
		});
		nodeIdSet.add(node.id);
	});

	edges
		.filter((edge) => nodeIdSet.has(edge.source) && nodeIdSet.has(edge.target))
		.forEach((edge) => {
			updatedGraph.setEdge(edge.source, edge.target);
		});

	Dagre.layout(updatedGraph);

	const updatedNodes = nodes.map((node) => {
		const nodeWithPosition = updatedGraph.node(node.id);

		return {
			...node,
			position: {
				x: nodeWithPosition.x - nodeWithPosition.width / 2,
				y: nodeWithPosition.y - nodeWithPosition.height / 2,
			},
		};
	});

	return {
		updatedGraph,
		updatedNodes,
	};
};
